"use client"

import type React from "react"
import { useState } from "react"
import Card from "components/Card"
import type { EmployeeLeaveInfoType } from "types/leaves-types"

interface LeavesInfoPropsType {
  leaveData: EmployeeLeaveInfoType[]
}

const LeavesInfo: React.FC<LeavesInfoPropsType> = ({ leaveData }) => {
  const [expandedCards, setExpandedCards] = useState<{ [key: number]: boolean }>({})

  const toggleCard = (index: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const getProgressPercentage = (taken: number, allowed: number) => {
    if (allowed === 0) return 0
    return Math.min((taken / allowed) * 100, 100)
  }

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return "bg-red-500"
    if (percentage >= 70) return "bg-yellow-500"
    return "bg-green-500"
  }

  // Filter data to only show current year
  const filterCurrentYearData = (data: EmployeeLeaveInfoType[]): EmployeeLeaveInfoType[] => {
    if (!data || data.length === 0) return []
    
    const currentYear = new Date().getFullYear()
    
    return data.filter((leaveInfo) => {
      if (!leaveInfo.period_start) return false
      
      // Only include leave info where period_start is in the current year
      // This ensures we only show current year allocations
      const periodStartYear = new Date(leaveInfo.period_start).getFullYear()
      
      return periodStartYear === currentYear
    })
  }

  // Consolidate duplicate leave types
  const consolidateLeaveData = (data: EmployeeLeaveInfoType[]): EmployeeLeaveInfoType[] => {
    if (!data || data.length === 0) return []

    const consolidated: { [key: string]: EmployeeLeaveInfoType } = {}

    data.forEach((leaveInfo) => {
      const leaveType = leaveInfo.leave_type_description
      
      if (consolidated[leaveType]) {
        consolidated[leaveType] = {
          ...consolidated[leaveType],
          max_leaves_allowed: (consolidated[leaveType].max_leaves_allowed || 0) + (leaveInfo.max_leaves_allowed || 0),
          remaining_leaves: (consolidated[leaveType].remaining_leaves || 0) + (leaveInfo.remaining_leaves || 0)
        }
      } else {
        consolidated[leaveType] = { ...leaveInfo }
      }
    })

    return Object.values(consolidated)
  }

  // First filter to current year, then consolidate
  const currentYearData = filterCurrentYearData(leaveData)
  const uniqueLeaveData = consolidateLeaveData(currentYearData)

  return (
    <Card extra="w-full h-full p-6">
      <div className="mb-6">
        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Employee Leaves Information</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">Overview of your leave allowances and usage</p>
      </div>

      <div className="space-y-4">
        {uniqueLeaveData?.map((leaveInfo: EmployeeLeaveInfoType, index: number) => {
          const taken = (leaveInfo.max_leaves_allowed || 0) - (leaveInfo.remaining_leaves || 0)
          const allowed = leaveInfo.max_leaves_allowed || 0
          const remaining = leaveInfo.remaining_leaves || 0
          const progressPercentage = getProgressPercentage(taken, allowed)
          const isExpanded = expandedCards[index]

          return (
            <div 
              key={index} 
              className="bg-gray-50 dark:bg-gray-800/50 rounded-lg overflow-hidden transition-all duration-300"
            >
              {/* Clickable Header */}
              <button
                onClick={() => toggleCard(index)}
                className="w-full p-4 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              >
                {/* Leave Type Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <h5 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {leaveInfo.leave_type_description}
                    </h5>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {taken}/{allowed} used
                    </div>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-gray-400 text-sm transition-transform duration-200`}></i>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(progressPercentage)}`}
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </button>

              {/* Expandable Stats Grid */}
              <div 
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isExpanded ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-3 gap-4 pt-2">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center border border-gray-200 dark:border-gray-700">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="fas fa-calendar-check text-blue-600 dark:text-blue-400 text-sm"></i>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Allowed</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{allowed}</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center border border-gray-200 dark:border-gray-700">
                      <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="fas fa-calendar-minus text-orange-600 dark:text-orange-400 text-sm"></i>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Taken</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{taken}</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center border border-gray-200 dark:border-gray-700">
                      <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="fas fa-calendar-plus text-green-600 dark:text-green-400 text-sm"></i>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Remaining</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{remaining}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Empty State */}
      {(!uniqueLeaveData || uniqueLeaveData.length === 0) && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-calendar-times text-2xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Leave Information</h3>
          <p className="text-gray-500 dark:text-gray-400">No leave data available at this time.</p>
        </div>
      )}
    </Card>
  )
}

export default LeavesInfo